import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { CLUB_EDITIONS, SCENES } from "@/lib/watchImages";
import WatchImage from "@/components/site/WatchImage";

const HERO_BG = SCENES.landingHero;

// Flagship hero watch — Azure Club Edition (CRS-I · BD)
const HERO_WATCH = CLUB_EDITIONS[0];

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="CLEMENT clubhouse terrace overlooking the course at golden hour"
          className="w-full h-full object-cover"
        />
        {/* warmer cinematic gradient — less flat black, more luxe */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C0F0A]/55 via-[#1F382A]/30 to-[#0C0F0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C0F0A]/85 via-transparent to-[#0C0F0A]/15" />
        {/* Gold sunlight haze top-right */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_0%,rgba(197,160,89,0.22),transparent_55%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 sm:pt-40 sm:pb-28 min-h-[100svh] flex flex-col">
        {/* Top overline row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex items-center gap-3"
        >
          <span className="gold-rule" />
          <span className="overline">The House · Course I</span>
        </motion.div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mt-10 sm:mt-14">
          {/* Headline */}
          <div className="lg:col-span-7">
            <motion.h1
              data-testid={TID.heroTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15, ease: "easeOut" }}
              className="font-serif-display font-light text-[#F5F2EB] text-balance leading-[0.95] text-[clamp(3.4rem,9vw,8.5rem)]"
            >
              CLEMENT
              <span className="block text-[#C5A059] italic font-light text-[clamp(2.2rem,5vw,5rem)] mt-2 tracking-wide">
                Course&nbsp;I
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
              className="mt-8 max-w-xl"
            >
              <p className="text-[#F5F2EB]/85 text-lg sm:text-xl leading-relaxed font-light">
                A mechanical golf timepiece, produced in measured allocations
                and released only when each piece meets the house&apos;s
                standards.
              </p>
              <p className="overline mt-6">By Registry · The First Chapter</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <a
                href="#waitlist"
                data-testid={TID.heroCta}
                className="inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-7 sm:px-9 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors duration-300"
              >
                Request Access
                <span aria-hidden className="inline-block w-6 h-px bg-current" />
              </a>
              <a
                href="#why"
                className="text-[11px] uppercase tracking-[0.28em] text-[#A8A9AD] hover:text-[#F5F2EB] transition-colors"
              >
                The House
              </a>
            </motion.div>
          </div>

          {/* Watch product */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.35, ease: "easeOut" }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
              <div className="absolute -inset-6 border border-[rgba(197,160,89,0.4)]" />
              <div className="absolute -inset-2 bg-gradient-to-br from-[#C5A059]/20 via-transparent to-[#1F382A]/40" />
              <WatchImage
                src={HERO_WATCH.front}
                alt={`CLEMENT COURSE I — ${HERO_WATCH.name} Club Edition`}
                fallbackTitle={HERO_WATCH.name}
                fallbackSub={HERO_WATCH.ref}
                loading="eager"
                className="drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
                testId="hero-watch-image"
              />
              <div className="absolute -bottom-4 left-4 right-4 flex justify-between text-[10px] uppercase tracking-[0.32em] text-[#C5A059]">
                <span>Ref. {HERO_WATCH.ref.replace("CRS-I · ", "CRS-I · ")}</span>
                <span>{HERO_WATCH.name}</span>
              </div>
            </div>
          </motion.div>
        </div>

        <div
          data-testid={TID.heroScrollDown}
          className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#A8A9AD] mt-12"
        >
          <span>Scroll</span>
          <span className="block w-12 h-px bg-[#A8A9AD]/50" />
        </div>
      </div>
    </section>
  );
}
